import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { AuthState, StaffUser, LoginCredentials } from '@/types';
import { authApi } from '@/services/api';
import toast from 'react-hot-toast';
import i18n from '../i18n';
import { logger } from '@/utils/logger';

interface AuthActions {
  login: (credentials: LoginCredentials) => Promise<boolean>;
  logout: () => void;
  refreshAuth: () => Promise<boolean>;
  updateUser: (user: Partial<StaffUser>) => void;
  clearError: () => void;
  setLoading: (loading: boolean) => void;
}

type AuthStore = AuthState & AuthActions;

export const useAuthStore = create<AuthStore>()(
  devtools(
    persist(
      (set, get) => ({
        // Initial state
        isAuthenticated: false,
        user: null,
        token: null,
        loading: false,
        error: null,

        // Actions
        login: async (credentials: LoginCredentials): Promise<boolean> => {
          try {
            set({ loading: true, error: null });

            const response = await authApi.login(credentials);
            
            if (response.data) {
              const { token, user } = response.data;
              
              set({
                isAuthenticated: true,
                user,
                token,
                loading: false,
                error: null,
              });

              // 存储token到localStorage（persist中间件会自动处理）
              localStorage.setItem('staff_token', token);

              toast.success(i18n.t('auth.welcomeBack', { name: user.full_name }));
              return true;
            } else {
              throw new Error(response.error || i18n.t('auth.loginFailed'));
            }
          } catch (error) {
            const responseError =
              typeof error === 'object' && error !== null && 'response' in error
                ? (error as { response?: { data?: { error?: string } } }).response?.data?.error
                : undefined;
            const message =
              responseError ||
              (error instanceof Error ? error.message : undefined) ||
              i18n.t('auth.loginFailedRetry');
            
            set({
              loading: false,
              error: message,
              isAuthenticated: false,
              user: null,
              token: null,
            });

            toast.error(message);
            return false;
          }
        },

        logout: () => {
          const existingToken = localStorage.getItem('staff_token');
          if (existingToken) {
            authApi.logout().catch(() => {
              // ignore remote logout errors; local logout must still complete
            });
          }

          // 清除本地存储
          localStorage.removeItem('staff_token');
          
          // 重置状态
          set({
            isAuthenticated: false,
            user: null,
            token: null,
            loading: false,
            error: null,
          });

          toast.success(i18n.t('auth.logoutSuccess'));
        },

        refreshAuth: async (): Promise<boolean> => {
          try {
            // Avoid concurrent refresh races that can leave the app in a loading state.
            if (get().loading) {
              return get().isAuthenticated;
            }

            const token = localStorage.getItem('staff_token');
            if (!token) {
              set({
                isAuthenticated: false,
                user: null,
                token: null,
                loading: false,
                error: null,
              });
              return false;
            }

            set({ loading: true });

            const refreshResponse = await Promise.race([
              authApi.refreshToken(),
              new Promise<never>((_, reject) =>
                setTimeout(() => reject(new Error('Auth refresh timeout')), 8000)
              ),
            ]);

            const refreshedToken =
              refreshResponse.data && 'token' in refreshResponse.data
                ? refreshResponse.data.token
                : null;
            const effectiveToken = refreshedToken || token;

            if (refreshedToken) {
              localStorage.setItem('staff_token', refreshedToken);
            }

            const response = await authApi.validateToken(effectiveToken);

            if (response.data) {
              set({
                isAuthenticated: true,
                user: response.data,
                token: effectiveToken,
                loading: false,
                error: null,
              });
              return true;
            } else {
              throw new Error('Token验证失败');
            }
          } catch (error) {
            logger.warn('Token刷新失败:', error);
            localStorage.removeItem('staff_token');
            set({
              isAuthenticated: false,
              user: null,
              token: null,
              loading: false,
              error: null,
            });
            return false;
          }
        },

        updateUser: (userData: Partial<StaffUser>) => {
          const currentUser = get().user;
          if (currentUser) {
            set({
              user: { ...currentUser, ...userData }
            });
          }
        },

        clearError: () => {
          set({ error: null });
        },

        setLoading: (loading: boolean) => {
          set({ loading });
        },
      }),
      {
        name: 'nexcave-staff-auth',
        partialize: (state) => ({
          // 只持久化这些字段
          isAuthenticated: state.isAuthenticated,
          user: state.user,
          token: state.token,
        }),
        onRehydrateStorage: () => () => {
          // App.tsx drives a single refreshAuth on startup.
          // Keeping refresh here can trigger duplicate concurrent refresh flows.
        },
      }
    ),
    {
      name: 'AuthStore',
    }
  )
);

// 选择器（优化性能）
export const useAuth = () => {
  const { isAuthenticated, user, token, loading, error } = useAuthStore();
  return { isAuthenticated, user, token, loading, error };
};

export const useAuthActions = () => {
  const { login, logout, refreshAuth, updateUser, clearError } = useAuthStore();
  return { login, logout, refreshAuth, updateUser, clearError };
};
